import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'

interface DropConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (cascade?: boolean) => void
  type: 'database' | 'schema' | 'table' | 'view' | 'function' | 'procedure' | 'trigger' | 'sequence' | 'extension' | 'index' | 'user'
  name: string
  database?: string
  schema?: string
  isLoading?: boolean
}

const getDropDetails = (type: string) => {
  switch (type) {
    case 'database':
      return {
        title: 'Drop Database',
        warning: 'This will permanently delete the entire database and all its contents, including all tables, views, functions, procedures, and data.',
        destructionLevel: 'critical',
        supportsCascade: false
      }
    case 'schema':
      return {
        title: 'Drop Schema',
        warning: 'This will permanently delete the schema and all its contents, including all tables, views, functions, procedures, and data within this schema.',
        destructionLevel: 'high',
        supportsCascade: true
      }
    case 'table':
      return {
        title: 'Drop Table',
        warning: 'This will permanently delete the table and all its data. Any dependent views, triggers, or constraints will also be affected.',
        destructionLevel: 'high',
        supportsCascade: true
      }
    case 'view':
      return {
        title: 'Drop View',
        warning: 'This will permanently delete the view. Any dependent objects may be affected.',
        destructionLevel: 'medium',
        supportsCascade: true
      }
    case 'function':
      return {
        title: 'Drop Function',
        warning: 'This will permanently delete the function. Any dependent objects that use this function may be affected.',
        destructionLevel: 'medium',
        supportsCascade: true
      }
    case 'procedure':
      return {
        title: 'Drop Procedure',
        warning: 'This will permanently delete the procedure. Any dependent objects that use this procedure may be affected.',
        destructionLevel: 'medium',
        supportsCascade: true
      }
    case 'trigger':
      return {
        title: 'Drop Trigger',
        warning: 'This will permanently delete the trigger. The associated table will no longer execute this trigger.',
        destructionLevel: 'medium',
        supportsCascade: true
      }
    case 'sequence':
      return {
        title: 'Drop Sequence',
        warning: 'This will permanently delete the sequence. Any columns or tables that depend on this sequence may be affected.',
        destructionLevel: 'medium',
        supportsCascade: true
      }
    case 'extension':
      return {
        title: 'Drop Extension',
        warning: 'This will permanently uninstall the extension. Any database objects that depend on this extension may be affected.',
        destructionLevel: 'medium',
        supportsCascade: true
      }
    case 'index':
      return {
        title: 'Drop Index',
        warning: 'This will permanently delete the index. Query performance on the associated table may be affected.',
        destructionLevel: 'low',
        supportsCascade: false
      }
    case 'user':
      return {
        title: 'Drop User',
        warning: 'This will permanently delete the user account. The user will no longer be able to access the database.',
        destructionLevel: 'medium',
        supportsCascade: false
      }
    default:
      return {
        title: 'Drop Object',
        warning: 'This will permanently delete the selected object.',
        destructionLevel: 'medium',
        supportsCascade: false
      }
  }
}

export function DropConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  type,
  name,
  database,
  schema,
  isLoading = false
}: DropConfirmDialogProps) {
  const [cascade, setCascade] = useState(false)
  
  if (!isOpen) return null

  const details = getDropDetails(type)

  const handleConfirm = () => {
    onConfirm(details.supportsCascade ? cascade : undefined)
  }

  const getDestructionLevelColor = (level: string) => {
    switch (level) {
      case 'critical': return 'text-red-600 dark:text-red-400'
      case 'high': return 'text-red-500 dark:text-red-400'
      case 'medium': return 'text-amber-600 dark:text-amber-400'
      case 'low': return 'text-yellow-600 dark:text-yellow-400'
      default: return 'text-gray-600 dark:text-gray-400'
    }
  }

  const getButtonColor = (level: string) => {
    switch (level) {
      case 'critical': return 'bg-red-700 hover:bg-red-800'
      case 'high': return 'bg-red-600 hover:bg-red-700'
      case 'medium': return 'bg-amber-600 hover:bg-amber-700'
      case 'low': return 'bg-yellow-600 hover:bg-yellow-700'
      default: return 'bg-red-600 hover:bg-red-700'
    }
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                    details.destructionLevel === 'critical' 
                      ? 'bg-red-100 dark:bg-red-900/30' 
                      : details.destructionLevel === 'high'
                      ? 'bg-red-100 dark:bg-red-900/30'
                      : 'bg-yellow-100 dark:bg-yellow-900/30'
                  }`}>
                    <ExclamationTriangleIcon className={`w-6 h-6 ${
                      details.destructionLevel === 'critical' || details.destructionLevel === 'high'
                        ? 'text-red-600 dark:text-red-400' 
                        : 'text-yellow-600 dark:text-yellow-400'
                    }`} />
                  </div>
                  <div>
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100"
                    >
                      {details.title}
                    </Dialog.Title>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {name}{database && ` from ${database}`}{schema && `.${schema}`}
                    </p>
                  </div>
                </div>

                <div className="mb-6 space-y-4">
                  <div className={`p-3 rounded-lg border ${
                    details.destructionLevel === 'critical' || details.destructionLevel === 'high'
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                      : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
                  }`}>
                    <p className={`text-sm font-medium ${getDestructionLevelColor(details.destructionLevel)}`}>
                      ⚠️ This action cannot be undone!
                    </p>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                      {details.warning}
                    </p>
                  </div>

                  {details.supportsCascade && (
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="cascade"
                        checked={cascade}
                        onChange={(e) => { setCascade(e.target.checked) }}
                        className="rounded border-gray-300 text-red-600 focus:ring-red-500"
                      />
                      <label 
                        htmlFor="cascade" 
                        className="text-sm text-gray-700 dark:text-gray-300"
                      >
                        Cascade delete (drop dependent objects)
                      </label>
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                    onClick={onClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className={`inline-flex justify-center rounded-md px-4 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${getButtonColor(details.destructionLevel)}`}
                    onClick={handleConfirm}
                    disabled={isLoading}
                  >
                    {isLoading && (
                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                    )}
                    {details.title}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
